import authSelectors from 'src/modules/auth/authSelectors';
import layoutSelectors from 'src/modules/layout/layoutSelectors';
import React, {
  useEffect,
  useRef,
  Suspense,
  useMemo,
  ElementType,
} from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router';
import renderEmailUnverifiedRoute from 'src/view/shared/routes/EmailUnverifiedRoute';
import renderPrivateRoute from 'src/view/shared/routes/PrivateRoute';
import renderPublicRoute from 'src/view/shared/routes/PublicRoute';
import Layout from 'src/view/layout/Layout';
import ProgressBar from 'src/view/shared/ProgressBar';
import routes from 'src/view/routes';
import renderEmptyTenantRoute from 'src/view/shared/routes/EmptyTenantRoute';
import renderEmptyPermissionsRoute from 'src/view/shared/routes/EmptyPermissionsRoute';
import LoadingComponent from 'src/view/shared/LoadingComponent';
import PermissionChecker from 'src/modules/auth/permissionChecker';
import { mapRoutesComponents } from 'src/view/shared/routes/helpers';

const mappedPublicRoutes = mapRoutesComponents(routes.publicRoutes);
const mappedPrivateRoutes = mapRoutesComponents(routes.privateRoutes);
const mappedEmailUnverifiedRoutes = mapRoutesComponents(
  routes.emailUnverifiedRoutes,
);
const mappedEptyTenantRoutes = mapRoutesComponents(routes.emptyTenantRoutes);
const mappedEmptyPermissionsRoutes = mapRoutesComponents(
  routes.emptyPermissionsRoutes,
);
const mappedeSimpleRoutes = mapRoutesComponents(routes.simpleRoutes);

function RoutesComponent() {
  const isInitialMount = useRef(true);
  const location = useLocation();
  const authLoading = useSelector(authSelectors.selectLoadingInit);
  const layoutLoading = useSelector(layoutSelectors.selectLoading);
  const loading = authLoading || layoutLoading;
  const currentUser = useSelector(authSelectors.selectCurrentUser);
  const currentTenant = useSelector(authSelectors.selectCurrentTenant);

  const permissionChecker = useMemo(() => {
    return new PermissionChecker(currentTenant, currentUser);
  }, [currentTenant, currentUser]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      ProgressBar.start();
      return;
    }

    if (!loading) {
      ProgressBar.done();
    }
  }, [loading]);

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <Layout
      hasSubroutes={['library', 'admin-panel', 'tools'].some(path =>
        location.pathname.includes(path),
      )}
      isLoggedIn={Boolean(currentUser)}
    >
      <Suspense fallback={<LoadingComponent />}>
        <Routes>
          {renderPublicRoute(mappedPublicRoutes, permissionChecker)}
          {renderEmailUnverifiedRoute(
            mappedEmailUnverifiedRoutes,
            permissionChecker,
          )}

          {renderEmptyTenantRoute(mappedEptyTenantRoutes, permissionChecker)}
          {renderEmptyPermissionsRoute(
            mappedEmptyPermissionsRoutes,
            permissionChecker,
          )}

          {renderPrivateRoute(mappedPrivateRoutes, permissionChecker, location)}

          {mappedeSimpleRoutes.map(route => {
            const Component = route.component as ElementType;

            return (
              <Route
                key={route.path}
                path={route.path}
                element={<Component />}
              />
            );
          })}
        </Routes>
      </Suspense>
    </Layout>
  );
}

export default RoutesComponent;
